/**
 * TemplatePickerDialog Component
 * Compact modal dialog for selecting a document template
 */

import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  FileText,
  Users,
  Target,
  Lightbulb,
  LayoutGrid,
  Check,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useDocTemplates } from '@/hooks/useDocTemplates'
import type { DocTemplateResponse } from '@ispot/shared'

interface TemplatePickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (template: DocTemplateResponse, title: string) => void
  isCreating?: boolean
}

// Domain config
const DOMAIN_CONFIG = {
  Market: { icon: Users, color: 'text-market', bg: 'bg-market/10' },
  Business: { icon: Target, color: 'text-business', bg: 'bg-business/10' },
  Solution: { icon: Lightbulb, color: 'text-solution', bg: 'bg-solution/10' },
}

export function TemplatePickerDialog({
  open,
  onOpenChange,
  onSelect,
  isCreating = false,
}: TemplatePickerDialogProps) {
  const { data: templates = [], isLoading } = useDocTemplates()
  const [selectedTemplate, setSelectedTemplate] = useState<DocTemplateResponse | null>(null)
  const [title, setTitle] = useState('')

  const handleSelect = (template: DocTemplateResponse) => {
    setSelectedTemplate(template)
    if (!title) {
      setTitle(`${template.name} - ${new Date().toLocaleDateString()}`)
    }
  }

  const handleCreate = () => {
    if (selectedTemplate && title.trim()) {
      onSelect(selectedTemplate, title.trim())
      setSelectedTemplate(null)
      setTitle('')
    }
  }

  const handleClose = () => {
    setSelectedTemplate(null)
    setTitle('')
    onOpenChange(false)
  }

  const getSectionCount = (template: DocTemplateResponse): number => {
    const structure = template.structure as { sections?: unknown[] }
    return structure?.sections?.length || 0
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b border-warm-200">
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            <LayoutGrid className="w-4 h-4 text-business" />
            New from Template
          </DialogTitle>
        </DialogHeader>

        {/* Template List */}
        <div className="max-h-[320px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-text-muted">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No templates available</p>
            </div>
          ) : (
            <div className="divide-y divide-warm-100">
              {templates.map((template) => {
                const domain = template.domain as keyof typeof DOMAIN_CONFIG
                const config = DOMAIN_CONFIG[domain] || DOMAIN_CONFIG.Business
                const Icon = config.icon
                const isSelected = selectedTemplate?.id === template.id

                return (
                  <button
                    key={template.id}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                      'hover:bg-warm-50',
                      isSelected && 'bg-business/5'
                    )}
                    onClick={() => handleSelect(template)}
                  >
                    <div className={cn('w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0', config.bg)}>
                      <Icon className={cn('w-4 h-4', config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{template.name}</span>
                        <span className={cn('text-[10px] uppercase font-medium', config.color)}>
                          {domain}
                        </span>
                      </div>
                      <div className="text-xs text-text-muted">
                        {getSectionCount(template)} sections
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-business flex-shrink-0" />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-warm-200 bg-warm-50 space-y-3">
          <Input
            placeholder="Document title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!selectedTemplate || isCreating}
            className="h-8 text-sm"
          />
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={isCreating}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={!selectedTemplate || !title.trim() || isCreating}
              className="flex-1"
            >
              {isCreating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
